import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { api } from '@/api/client'
import { PageHeader } from '@/app/shell'
import { Card, EmptyState, Pill, Skeleton } from '@/ui/primitives'
import { NewMenu } from '@/ui/new-menu'
import { lei } from '@/lib/money'

type Sarcina = {
  id: string; type: 'departure' | 'invoice_due' | 'payment_due'
  urgency: 'high' | 'medium' | 'low'
  title: string; message: string; detail?: string; date?: string
  amount?: number; invoice_id?: number; reservation_id?: number
}
type Raspuns = {
  notifications: Sarcina[]
  summary: { total: number; high: number; medium: number; low: number; by_type: Record<string, number> }
}

const FILTRE = [
  { id: 'toate', eticheta: 'Toate' },
  { id: 'departure', eticheta: 'Plecari' },
  { id: 'invoice_due', eticheta: 'De incasat' },
  { id: 'payment_due', eticheta: 'De platit' },
]

const CULOARE = { high: 'bg-bad', medium: 'bg-warn', low: 'bg-line-strong' }

/** Anul apare doar cand nu e cel curent — multe restante sunt din anii trecuti si
 *  „09 sept." fara an arata ca o scadenta viitoare. */
const zi = (s: string) => {
  const d = new Date(s)
  return d.toLocaleDateString('ro-RO', d.getFullYear() === new Date().getFullYear()
    ? { day: '2-digit', month: 'short' }
    : { day: '2-digit', month: 'short', year: 'numeric' })
}

/** Sarcinile = ce cere atentie azi, din notificarile reale ale aplicatiei:
 *  plecari in 48h, facturi scadente, plati catre furnizori. */
export function Tasks() {
  const nav = useNavigate()
  const [filtru, setFiltru] = useState('toate')
  const { data, isPending } = useQuery({
    queryKey: ['sarcini'], queryFn: () => api.get<Raspuns>('/dashboard/notifications'),
  })

  const toate = data?.notifications ?? []
  const lista = filtru === 'toate' ? toate : toate.filter((n) => n.type === filtru)

  const deschide = (n: Sarcina) => {
    if (n.reservation_id) nav(`/rezervari/${n.reservation_id}`)
    else if (n.invoice_id) nav(`/bani/facturi/${n.invoice_id}`)
  }

  return (
    <>
      <PageHeader title="Sarcini"
        count={data ? `${toate.length} afisate din ${data.summary.total} · ${data.summary.high} urgente` : undefined}
        actions={<NewMenu />} />
      <div className="px-8 py-6 space-y-4">
        <div className="flex flex-wrap gap-2">
          {FILTRE.map((f) => (
            <Pill key={f.id} active={filtru === f.id} onClick={() => setFiltru(f.id)}
              count={f.id === 'toate' ? toate.length : toate.filter((n) => n.type === f.id).length}>
              {f.eticheta}
            </Pill>
          ))}
        </div>

        {isPending ? <Card><Skeleton /></Card> : lista.length === 0 ? (
          <Card><EmptyState title="Nimic de rezolvat" hint="Nicio plecare apropiata si nicio scadenta depasita." /></Card>
        ) : (
          <Card className="divide-y divide-line overflow-hidden">
            {lista.map((n) => (
              <button key={n.id} onClick={() => deschide(n)}
                className="w-full text-left px-5 py-3.5 flex items-start gap-3.5 hover:bg-page transition-colors">
                <span className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${CULOARE[n.urgency]}`} aria-hidden />
                <span className="min-w-0 flex-1">
                  <span className="block text-[14.5px]">{n.title}</span>
                  <span className="block text-[13.5px] text-muted truncate">{n.message}</span>
                  {n.detail && <span className="block text-[12.5px] text-muted truncate">{n.detail}</span>}
                </span>
                <span className="text-right shrink-0">
                  {n.amount !== undefined && <span className="block tnum text-[14px]">{lei(n.amount)} lei</span>}
                  {n.date && <span className="block text-[12.5px] text-muted tnum">{zi(n.date)}</span>}
                </span>
              </button>
            ))}
          </Card>
        )}
      </div>
    </>
  )
}
